import { useEffect, useState } from "react";

const isTextField = (el) => el && (el.tagName === "INPUT" || el.tagName === "TEXTAREA");

/**
 * True while the on-screen keyboard is open. iOS shrinks only the visual viewport, so a bar
 * pinned to the bottom would ride up over the page: callers hide it while this is true.
 *
 * A focused text field counts as open straight away (the viewport only shrinks once the
 * keyboard has slid up), but a keyboard dismissed with its own hide key leaves the field
 * focused, so once the viewport has been seen shrinking, growing back means closed.
 */
export default function useKeyboardOpen() {
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const vv = window.visualViewport;
    let seenShrunk = false;
    const isShrunk = () => (vv ? window.innerHeight - vv.height > 120 : false);
    const check = () => setOpen(isShrunk() || isTextField(document.activeElement));
    const onViewportResize = () => {
      if (isShrunk()) {
        seenShrunk = true;
        setOpen(true);
      } else if (seenShrunk) {
        seenShrunk = false;
        setOpen(false);
      }
    };
    const onFocusOut = () => {
      seenShrunk = false;
      setTimeout(check, 0);
    };
    vv?.addEventListener("resize", onViewportResize);
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", onFocusOut);
    return () => {
      vv?.removeEventListener("resize", onViewportResize);
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  return open;
}
